import { Box, FormControl, InputLabel, Select, MenuItem, OutlinedInput, FormHelperText, SelectChangeEvent, Stack } from '@mui/material'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IMeter, IPostMeter } from '../../../shared/services/location/location.model'
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model'
import { getSelectableSubsidiaries } from '../../../shared/utils/subsidiary-utils'
import { EmissionsFilterLogic } from '../../presentation-components/emissons-components/overview/emissions-filter/emissions-filter-logic'
import { IConversionSource } from '../../../shared/services/conversion-source/conversion-source.model';
import ConversionSourceService from '../../../shared/services/conversion-source/conversion-source.service';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { FunctionComponent, useContext, useEffect, useState } from 'react';

export const MeterInputDialog: FunctionComponent<{
  meter?: IMeter,
  isEdit: boolean,
  open: boolean;
  onClose: () => void;
  onSubmit: (values: IPostMeter, restForm: () => void, isEdit: boolean) => void;
  locationId: string;
  isSubmitting?: boolean;
  errorMessage?: string | null,
  subsidiaryId: string;
}> = ({
  meter,
  isEdit,
  open,
  onClose,
  onSubmit,
  locationId,
  isSubmitting = false,
  errorMessage,
  subsidiaryId
}) => {
    // Services
    const subsidiaryService = SubsidiaryService.getInstance();
    const conversionSourceService = new ConversionSourceService();
    //Context
    const userDetailsContext = useContext(UserDetailsContext);
    const organisationDetails = userDetailsContext?.userDetails?.organisation;
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [conversionSourceId, setConversionSourceId] = useState<string>('5c7f57de-5209-4851-bd5e-eb1bb0e5273c')
    const [groupId, setGroupId] = useState<string>('acf9ae04-25d9-43d7-b442-fabd69e5844b')
    const [categoryId, setCategoryId] = useState<string>('df026bd9-281d-4013-80e4-a1ef92965502')
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>([]);
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([]);

    //Formik 
    const validationSchema = Yup.object().shape({
      name: Yup.string().required('Name is required'),
      subsidiaryId: Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
      groupId: Yup.string().required('Group is required'),
      returnId: Yup.string().when('isEdit', {
        is: false,
        then: schema => schema.required('You must provide a return'),
        otherwise: schema => schema.notRequired()
      })
    });

    const formik = useFormik({
      initialValues: {
        name: '',
        conversionSourceId: '',
        groupName: '',
        groupId: '',
        categoryName: '',
        categoryId: '',
        detailLevelOne: '',
        detailLevelTwo: '',
        detailLevelThree: '',
        detailLevelFour: '',
        detailLevelFive: '',
        measurementUnitName: '',
        returnId: '',
        subsidiaryId: '',
      },
      validationSchema,
      onSubmit: async (values) => {
        const meterData = {
          name: values.name.trim(),
          locationId: locationId,
          conversionId: values.returnId,
          subsidiaryId: values.subsidiaryId,
          groupId: values.groupId,
        };
        onSubmit(meterData, formik.resetForm, isEdit)
      },
    });

    useEffect(() => {
      if (open && !isEdit && !meter) {
        formik.setValues({
          name: '',
          conversionSourceId: '5c7f57de-5209-4851-bd5e-eb1bb0e5273c',
          groupName: 'Purchased Energy',
          groupId: 'acf9ae04-25d9-43d7-b442-fabd69e5844b',
          categoryName: 'Electricity',
          categoryId: 'df026bd9-281d-4013-80e4-a1ef92965502',
          detailLevelOne: '',
          detailLevelTwo: '',
          detailLevelThree: '',
          detailLevelFour: '',
          detailLevelFive: '',
          measurementUnitName: '',
          returnId: '',
          subsidiaryId: formik.values.subsidiaryId || '',
        });
        setConversionSourceId('5c7f57de-5209-4851-bd5e-eb1bb0e5273c');
        setGroupId('acf9ae04-25d9-43d7-b442-fabd69e5844b');
        setCategoryId('df026bd9-281d-4013-80e4-a1ef92965502');
      }
    }, [open, isEdit, meter]);

    useEffect(() => {
      if (isEdit && meter) {
        formik.setValues({
          name: meter.name,
          returnId: meter.conversionId,
          subsidiaryId: meter.subsidiaryId || '',
          groupId: meter.groupId || '',
          groupName: meter.group?.title || '',
          conversionSourceId: meter.conversion?.conversionSourceId || '',
          categoryId: meter.conversion?.category?.id || '',
          categoryName: meter.conversion?.category?.id === 'f88cd55e-9e30-4605-8106-24a53e0b5f62' ? 'Gas' : 'Electricity',
          detailLevelOne: meter.conversion?.detailLevelOne || '',
          // detailLevelOne: meter.conversion?.category?.id === 'f88cd55e-9e30-4605-8106-24a53e0b5f62' ? 'Fuels' : meter.conversion?.detailLevelOne || '',
          detailLevelTwo: meter.conversion?.detailLevelTwo || '',
          detailLevelThree: meter.conversion?.detailLevelThree || '',
          detailLevelFour: meter.conversion?.detailLevelFour || '',
          detailLevelFive: meter.conversion?.detailLevelFive || '',
          measurementUnitName: meter.conversion?.measurementUnit?.name || '',
        });
        if (meter.conversion?.conversionSourceId) {
          setConversionSourceId(meter.conversion.conversionSourceId);
        }
        if (meter.groupId) {
          setGroupId(meter.groupId);
        }
        if (meter.conversion?.category?.id) {
          setCategoryId(meter.conversion.category.id);
        }
      }
    }, [isEdit, meter]);

    useEffect(() => {
      if (open && !meter && subsidiaryId && subsidiaries.length > 0) {
        const subsidiaryExists = subsidiaries.some(s => s.id === subsidiaryId);
        if (subsidiaryExists) {
          formik.setFieldValue('subsidiaryId', subsidiaryId);
        }
      }
    }, [open, meter, subsidiaryId, subsidiaries]);

    useEffect(() => {
      conversionSourceService.getAll(true, 'conversion')
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => console.log(err))

      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response);
          if (response.length === 1) {
            formik.setFieldValue('subsidiaryId', response[0].id)
          } else if (subsidiaryId && !meter) {
            const subsidiaryExists = response.some(s => s.id === subsidiaryId);
            if (subsidiaryExists) {
              formik.setFieldValue('subsidiaryId', subsidiaryId)
            }
          }
        })
        .catch(err => {
          console.error(err);
        });
    }, []);

    const conversionSourceHandler = (e: SelectChangeEvent) => {
      if (e.target.value !== formik.values.conversionSourceId) {
        formik.setFieldValue('conversionSourceId', e.target.value);
        setConversionSourceId(e.target.value)
      }
    }

    const selectCategoryHandler = (value: string) => {
      if (value === formik.values.categoryName) {
        return;
      }

      formik.setFieldValue('categoryName', value);

      if (value === 'Electricity') {
        formik.setFieldValue('groupName', 'Purchased Energy');
        formik.setFieldValue('groupId', 'acf9ae04-25d9-43d7-b442-fabd69e5844b');
        setGroupId('acf9ae04-25d9-43d7-b442-fabd69e5844b');
        formik.setFieldValue('categoryId', 'df026bd9-281d-4013-80e4-a1ef92965502');
        setCategoryId('df026bd9-281d-4013-80e4-a1ef92965502');
      }

      if (value === 'Gas') {
        formik.setFieldValue('groupName', 'Company Facilities');
        formik.setFieldValue('groupId', '22cc8b45-aa77-49aa-8fb2-c39bb884fa13');
        setGroupId('22cc8b45-aa77-49aa-8fb2-c39bb884fa13');
        formik.setFieldValue('categoryId', 'f88cd55e-9e30-4605-8106-24a53e0b5f62');
        setCategoryId('f88cd55e-9e30-4605-8106-24a53e0b5f62');
      }

      formik.setFieldValue('detailLevelOne', '');
      formik.setFieldValue('detailLevelTwo', '');
      formik.setFieldValue('detailLevelThree', '');
      formik.setFieldValue('detailLevelFour', '');
      formik.setFieldValue('detailLevelFive', '');
      formik.setFieldValue('measurementUnitName', '');
      formik.setFieldValue('returnId', '');
    }

    const handleClose = () => {
      formik.resetForm();
      onClose();
    };

    return (
      <DialogWrapper
        open={open}
        onClose={handleClose}
        title={isEdit ? "Edit Meter" : "Add Meter"}
        cancelHandler={handleClose}
        submitHandler={formik.submitForm}
        disableSaveButton={isSubmitting || !formik.isValid}
        isSubmitting={isSubmitting}>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='name'>Name</InputLabel>
          <OutlinedInput id='name' label='Name' name='name' type='text'
            value={formik.values.name}
            onChange={formik.handleChange}
            disabled={isSubmitting}
            error={formik.touched.name && Boolean(formik.errors.name)}
            sx={{ color: 'text.primary' }}></OutlinedInput>
          {formik.touched.name && formik.errors.name &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.name}</FormHelperText>
          }
        </FormControl>
        {organisationDetails?.enableSubsidiaries &&
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
            <Select
              name="subsidiaryId"
              value={formik.values.subsidiaryId}
              onChange={formik.handleChange}
              error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
              disabled={isSubmitting}
              label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}
            >
              {getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).map((subsidiary) => (
                <MenuItem key={subsidiary.id} value={subsidiary.id}>
                  {subsidiary.name}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.subsidiaryId}</FormHelperText>
            }
          </FormControl>
        }
        {conversionSources &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='conversionSourceId'>Conversion Factor Source</InputLabel>
            <Select
              id='conversionSourceId' label='Conversion Factor Source' name='conversionSourceId'
              value={formik.values.conversionSourceId || ''}
              onChange={conversionSourceHandler}
              error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
              sx={{ color: 'text.primary', }}>
              {conversionSources.filter(source => source.id !== '6aa98e49-bab0-4863-bbc1-25f91d15a5db').sort((a, b) => a.source.localeCompare(b.source)).map((conversionSource, index) => (
                <MenuItem key={index} value={conversionSource.id}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <span>{conversionSource.source}</span>
                    {conversionSource.country && (
                      <img
                        loading="lazy"
                        width="20"
                        src={conversionSource.country.imageUrl}
                        alt={`${conversionSource.country.name} flag`}
                      />
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
            {formik.touched.conversionSourceId && formik.errors.conversionSourceId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.conversionSourceId}</FormHelperText>
            }
          </FormControl>}
        <FormControl fullWidth variant='outlined' sx={{ mt: 2, mb: 2 }}>
          <InputLabel htmlFor='categoryName'>Category</InputLabel>
          <Select
            id='categoryName' label='Category' name='categoryName'
            value={formik.values.categoryName || ''}
            onChange={(e) => selectCategoryHandler(e.target.value as string)}
            error={formik.touched.categoryName && Boolean(formik.errors.categoryName)}
            sx={{ color: 'text.primary', }}>
            <MenuItem value={'Electricity'}>Electricity</MenuItem>
            <MenuItem value={'Gas'}>Gas</MenuItem>
          </Select>
          {formik.touched.categoryName && formik.errors.categoryName &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.categoryName}</FormHelperText>
          }
        </FormControl>
        {categoryId && groupId && conversionSourceId ?
          <EmissionsFilterLogic
            categoryId={categoryId}
            groupId={groupId}
            defaultConversionSourceId={conversionSourceId}
            formik={formik}
            isEdit={isEdit}
            direction='column' />
          :
          <FormControl fullWidth variant='outlined'>
            <InputLabel htmlFor='name'>Factor Detail 1</InputLabel>
            <OutlinedInput id='detailLevelOne' label='Factor Detail 1' name='detailLevelOne' type='text'
              disabled={true}
              sx={{ color: 'text.primary' }}></OutlinedInput>
          </FormControl>
        }
        <Box sx={{ mt: 2 }}>
        </Box>
        {errorMessage &&
          <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
            {errorMessage}
          </FormHelperText>
        }
      </DialogWrapper>
    );
  };
